import { randomId } from "@codeshare/protocol";
import type { Profile } from "@codeshare/session";
import { colorFor } from "@codeshare/theme";

export const IDENTITY_STORAGE_KEY = "codeshare:identity";
/** The tab using the saved identity, with a heartbeat: `{ tab, at }`. */
export const IDENTITY_HOLDER_KEY = "codeshare:identity-holder";
/** A second tab's own identity (sessionStorage: this tab only, kept across reloads). */
export const TAB_IDENTITY_KEY = "codeshare:tab-identity";
const HEARTBEAT_MS = 2_000;
const HOLDER_STALE_MS = 6_000;

const ADJECTIVES = [
  "Brave",
  "Calm",
  "Clever",
  "Cosmic",
  "Curious",
  "Gentle",
  "Lucky",
  "Nimble",
  "Quiet",
  "Sunny",
  "Swift",
  "Witty",
];
const ANIMALS = [
  "Badger",
  "Crane",
  "Falcon",
  "Fox",
  "Heron",
  "Koala",
  "Lynx",
  "Otter",
  "Panda",
  "Puffin",
  "Raven",
  "Tiger",
];

type Storage = Pick<globalThis.Storage, "getItem" | "setItem">;

function pick<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)] as T;
}

/** A friendly default display name, e.g. "Swift Otter". */
export function randomName(): string {
  return `${pick(ADJECTIVES)} ${pick(ANIMALS)}`;
}

/** This browser's identity: a stable client id plus display name and color (editable later). */
export function loadIdentity(
  storage: Storage | null = safeLocalStorage(),
  key = IDENTITY_STORAGE_KEY,
): Profile {
  try {
    const stored = JSON.parse(storage?.getItem(key) ?? "null");
    if (
      stored &&
      typeof stored.clientId === "string" &&
      typeof stored.name === "string" &&
      typeof stored.color === "string"
    ) {
      return { clientId: stored.clientId, name: stored.name, color: stored.color };
    }
  } catch {}
  const clientId = randomId();
  const identity = { clientId, name: randomName(), color: colorFor(clientId) };
  saveIdentity(identity, storage, key);
  return identity;
}

export function saveIdentity(
  identity: Profile,
  storage: Storage | null = safeLocalStorage(),
  key = IDENTITY_STORAGE_KEY,
): void {
  try {
    storage?.setItem(key, JSON.stringify(identity));
  } catch {}
}

function safeLocalStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function safeSessionStorage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

export interface ClaimedIdentity {
  identity: Profile;
  /** Where this tab saves profile edits. */
  storage: Storage | null;
  key: string;
  /** True when this tab holds the browser's saved identity (and must keep its heartbeat fresh). */
  holder: boolean;
}

/**
 * Which identity this tab uses. The first tab uses the browser's saved identity; a second tab
 * opened while that one is live becomes a different person (its own client id and name), so two
 * tabs in one browser can join a room as two people. Synchronous, so it's settled before anything
 * joins a room.
 */
export function claimIdentity(
  tab: string,
  local: Storage | null = safeLocalStorage(),
  session: Storage | null = safeSessionStorage(),
  now = Date.now(),
): ClaimedIdentity {
  const tabScoped = { storage: session, key: TAB_IDENTITY_KEY, holder: false };
  if (session?.getItem(TAB_IDENTITY_KEY)) {
    return { identity: loadIdentity(session, TAB_IDENTITY_KEY), ...tabScoped };
  }
  let holder: { tab?: unknown; at?: unknown } | null = null;
  try {
    holder = JSON.parse(local?.getItem(IDENTITY_HOLDER_KEY) ?? "null");
  } catch {}
  const otherTabLive =
    holder !== null &&
    holder.tab !== tab &&
    typeof holder.at === "number" &&
    now - holder.at < HOLDER_STALE_MS;
  if (otherTabLive) {
    // A new person, never sharing a name with the first tab's.
    const taken = loadIdentity(local).name;
    let name = randomName();
    while (name === taken) name = randomName();
    const clientId = randomId();
    const identity = { clientId, name, color: colorFor(clientId) };
    saveIdentity(identity, session, TAB_IDENTITY_KEY);
    return { identity, ...tabScoped };
  }
  return { identity: loadIdentity(local), storage: local, key: IDENTITY_STORAGE_KEY, holder: true };
}

/** Keeps this tab's claim on the saved identity fresh, and lets it go when the tab closes. */
function holdIdentity(tab: string, local: Storage | null = safeLocalStorage()): void {
  const beat = () => {
    try {
      local?.setItem(IDENTITY_HOLDER_KEY, JSON.stringify({ tab, at: Date.now() }));
    } catch {}
  };
  beat();
  setInterval(beat, HEARTBEAT_MS);
  globalThis.addEventListener?.("pagehide", () => {
    try {
      const holder = JSON.parse(local?.getItem(IDENTITY_HOLDER_KEY) ?? "null");
      if (holder?.tab === tab)
        (local as globalThis.Storage | null)?.removeItem(IDENTITY_HOLDER_KEY);
    } catch {}
  });
  // Back from the bfcache: claim it again.
  globalThis.addEventListener?.("pageshow", (event) => {
    if ((event as PageTransitionEvent).persisted) beat();
  });
}

/** The current identity, observable, persisted on change. */
export class IdentityStore {
  #identity: Profile;
  readonly #save: (identity: Profile) => void;
  readonly #listeners = new Set<() => void>();

  constructor(initial: Profile, save: (identity: Profile) => void = (i) => saveIdentity(i)) {
    this.#identity = initial;
    this.#save = save;
  }

  get(): Profile {
    return this.#identity;
  }

  /** Updates name and/or color (the client id never changes). */
  update(changes: Partial<Pick<Profile, "name" | "color">>): void {
    const name = changes.name?.trim().slice(0, 40);
    this.#identity = {
      ...this.#identity,
      ...(name ? { name } : {}),
      ...(changes.color ? { color: changes.color } : {}),
    };
    this.#save(this.#identity);
    for (const listener of this.#listeners) listener();
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }
}

let store: IdentityStore | undefined;
export function getIdentityStore(): IdentityStore {
  if (!store) {
    const tab = randomId();
    const claimed = claimIdentity(tab);
    if (claimed.holder) holdIdentity(tab);
    store = new IdentityStore(claimed.identity, (identity) =>
      saveIdentity(identity, claimed.storage, claimed.key),
    );
  }
  return store;
}
